class CrossSellDrawer extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector('cart-notification') ?? document.querySelector('cart-drawer');
    this.originalParent = null;
    this.originalNextSibling = null;
    this.isTeleported = false;
    this.transitionDuration = 300;
    this.focusableElements =
      'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    this.lastFocusedElement = null;

    this.overlay = null;
    this.inner = null;
  }

  connectedCallback() {
    this.initializeComponent();
    this.setupEventListeners();
  }

  initializeComponent() {
    this.overlay = this.querySelector('.drawer__overlay');
    this.inner = this.querySelector('.cross-sell-drawer__inner');

    const computedStyle = getComputedStyle(this);
    const cssTransitionDuration = computedStyle.getPropertyValue('--transition-duration');
    if (cssTransitionDuration) {
      this.transitionDuration = parseInt(cssTransitionDuration) || 300;
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    this.overlay?.addEventListener('click', this.handleOverlayClick);

    this.addEventListener('change', (e) => {
      if (e.target.classList.contains('cross-sell-drawer__variant-dropdown')) {
        this.handleVariantChange(e);
      }
    });

    this.addEventListener('click', (e) => {
      const closeButton = e.target.closest('[data-drawer-close], .cross-sell-drawer__close-btn');
      if (closeButton) {
        this.handleCloseClick(e);
        return;
      }

      const actionButton =
        e.target.closest('[data-action="add-to-cart"]') ??
        e.target.closest('.component-button[data-action="add-to-cart"]') ??
        e.target.closest('.cross-sell-drawer__add-btn');

      if (actionButton) {
        this.handleAddToCart(e);
      }
    });
  }

  removeEventListeners() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  handleOverlayClick = () => {
    this.close();
  };

  handleCloseClick = (e) => {
    e.preventDefault();
    this.close();
  };

  handleKeydown = (e) => {
    if (e.key === 'Escape') {
      this.close();
    }
    if (e.key === 'Tab') {
      this.trapFocus(e);
    }
  };

  handleVariantChange = (e) => {
    const { target: select } = e;
    const { productId, optionPosition } = select.dataset;
    const { value: selectedValue } = select;

    const swatchElement = this.querySelector(`[data-selected-swatch="${productId}-${optionPosition}"]`);
    if (swatchElement) {
      const selectedOption = select.querySelector(`option[value="${selectedValue}"]`);
      const swatchValue = selectedOption?.dataset.optionSwatchValue;

      if (swatchValue) {
        const swatchSpan = swatchElement.querySelector('.swatch');
        swatchSpan?.style.setProperty('--swatch--background', swatchValue);
      }
    }

    this.updateSelectedVariant(productId);
    this.updateVariantImage(productId);
  };

  updateSelectedVariant = (productId) => {
    const productItem = this.querySelector(`[data-product-id="${productId}"]`);
    if (!productItem) return;

    const variantDataScript = productItem.querySelector('.variant-data');
    if (!variantDataScript) return;

    let variantData;
    try {
      variantData = JSON.parse(variantDataScript.textContent);
    } catch {
      return;
    }

    const selects = productItem.querySelectorAll('.cross-sell-drawer__variant-dropdown');
    const selectedOptions = Array.from(selects).map((select) => select.value);

    const matchingVariant = variantData.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );

    if (matchingVariant) {
      const btnWrapper = productItem.querySelector('.cross-sell-drawer__btn-wrapper');
      if (btnWrapper) {
        btnWrapper.dataset.variantId = matchingVariant.id;
      }
    }
  };

  updateVariantImage = (productId) => {
    const productItem = this.querySelector(`[data-product-id="${productId}"]`);
    if (!productItem) return;

    const variantDataScript = productItem.querySelector('.variant-data');
    if (!variantDataScript) return;

    let variantData;
    try {
      variantData = JSON.parse(variantDataScript.textContent);
    } catch {
      return;
    }

    const selects = productItem.querySelectorAll('.cross-sell-drawer__variant-dropdown');
    const selectedOptions = Array.from(selects).map((select) => select.value);

    const matchingVariant = variantData.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );

    if (matchingVariant && matchingVariant.featured_image) {
      const imageElements = productItem.querySelectorAll('[data-selected-image]');
      imageElements.forEach((imageElement) => {
        const img = imageElement.querySelector('.dropdown-image__img');
        if (img) {
          img.src = matchingVariant.featured_image;
          img.alt = matchingVariant.title;
        }
      });
    }
  };

  handleAddToCart = async (e) => {
    e.preventDefault();

    const button =
      e.target.closest('[data-action="add-to-cart"]') ??
      e.target.closest('.component-button[data-action="add-to-cart"]') ??
      e.target.closest('.cross-sell-drawer__add-btn');
    if (!button) return;

    const wrapper = button.closest('.cross-sell-drawer__btn-wrapper');
    const variantId = wrapper?.dataset.variantId ?? button.dataset.variantId;
    const productTitle = wrapper?.dataset.productTitle ?? button.dataset.productTitle;

    if (!variantId) {
      console.error('[CrossSellDrawer] No variant ID found on button');
      return;
    }

    button.setAttribute('aria-disabled', true);
    button.classList.add('cross-sell-drawer__add-btn--loading');
    button.disabled = true;

    const productItem = button.closest('.cross-sell-drawer__product-item');
    const quantityInput = productItem?.querySelector('.quantity__input');
    const quantity = parseInt(quantityInput?.value) || 1;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity.toString());

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    if (this.cart?.getSectionsToRender) {
      const sections = this.cart.getSectionsToRender().map((section) => section.id);
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      this.cart.setActiveElement?.(document.activeElement);
    }

    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const result = await response.json();

      if (result.status) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cross-sell-drawer',
            productVariantId: variantId,
            errors: result.errors ?? result.description,
            message: result.message,
          });
        }
        console.error('[CrossSellDrawer] Add to cart error:', result.description);
        return;
      } else if (!this.cart) {
        window.location = window.routes.cart_url;
        return;
      }

      this.close();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cross-sell-drawer',
          productVariantId: variantId,
          cartData: result,
        });
      }

      this.cart?.renderContents?.(result);
    } catch (error) {
      console.error('[CrossSellDrawer] Add to cart failed:', error);
    } finally {
      button.classList.remove('cross-sell-drawer__add-btn--loading');
      button.disabled = false;
      button.removeAttribute('aria-disabled');

      if (this.cart?.classList.contains('is-empty')) {
        this.cart.classList.remove('is-empty');
      }
    }
  };

  open() {
    this.lastFocusedElement = document.activeElement;
    this.teleportToBody();
    this.hidden = false;
    this.setAttribute('aria-hidden', 'false');

    this.setPageInert(true);
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.handleKeydown);

    requestAnimationFrame(() => {
      this.classList.add('active');
      this.focusFirstElement();
    });
  }

  close() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.classList.remove('active');

    setTimeout(() => {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.restoreFromBody();
      this.setPageInert(false);
      document.body.style.overflow = '';
      this.restoreFocus();
    }, this.transitionDuration);
  }

  teleportToBody() {
    if (this.isTeleported) return;

    this.originalParent = this.parentElement;
    this.originalNextSibling = this.nextElementSibling;

    document.body.appendChild(this);
    this.isTeleported = true;
  }

  restoreFromBody() {
    if (!this.isTeleported || !this.originalParent) return;

    if (this.originalNextSibling) {
      this.originalParent.insertBefore(this, this.originalNextSibling);
    } else {
      this.originalParent.appendChild(this);
    }

    this.isTeleported = false;
    this.originalParent = null;
    this.originalNextSibling = null;
  }

  focusFirstElement() {
    const closeButton = this.querySelector('[data-drawer-close]');
    if (closeButton) {
      closeButton.focus();
      return;
    }

    this.setAttribute('tabindex', '-1');
    this.focus();
  }

  restoreFocus() {
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }
  }

  setPageInert(inert) {
    Array.from(document.body.children).forEach((child) => {
      if (child !== this && !['SCRIPT', 'STYLE'].includes(child.tagName)) {
        child.inert = inert;
      }
    });
  }

  trapFocus(e) {
    const focusableElements = this.querySelectorAll(this.focusableElements);
    const [firstFocusable] = focusableElements;
    const lastFocusable = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstFocusable) {
      lastFocusable.focus();
      e.preventDefault();
    } else if (!e.shiftKey && document.activeElement === lastFocusable) {
      firstFocusable.focus();
      e.preventDefault();
    }
  }
}

customElements.define('cross-sell-drawer', CrossSellDrawer);
